import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { EmptyState, EMPTY_STATE_SIZE_XS, Spinner } from '@aibuild/ui';
import MessageBox, {
  MESSAGE_BOX_VARIANT_ERROR,
} from '@components/2-molecules/MessageBox';
import ToolChip from '@components/Chat/ToolChip';
import type { ToolExecution } from '@hooks/useChat';
import type { TranscriptEntry } from '../runSession/types';
import NodeSection from '../nodes/NodeSection';
import { GENERIC_NODE_FAILURE } from '../failureCopy';
import {
  CollapseToggle,
  DetailScroll,
  ErrorScroll,
  LogCard,
  LogHeader,
  LogHeaderLeft,
  LogTitle,
  TextBlock,
  ToolRow,
  TranscriptInner,
  TranscriptScroll,
} from './NodeDetailPanel.styled';

// Pixels of slack we allow at the bottom edge before declaring the user
// has manually scrolled up. Tracks chat's tolerance window — wide enough
// to absorb the height of one tool chip and a line of text so a single
// new entry doesn't accidentally "scroll away" from the user.
const AT_BOTTOM_TOLERANCE_PX = 40;

// sessionStorage key for the collapse preference. Session-scoped
// (matches the dock-collapsed preference one level up) so a user who
// minimised the log keeps it minimised as they hop between nodes, but
// a fresh tab starts expanded so the log is visible by default.
const COLLAPSE_STORAGE_KEY = 'proc-node-log-collapsed';

const readCollapsedPref = (): boolean => {
  try {
    return sessionStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeCollapsedPref = (collapsed: boolean): void => {
  try {
    sessionStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    /* sessionStorage unavailable (private mode, quota) — no-op */
  }
};

export interface NodeDetailPanelProps {
  readonly errorMessage: string | null;
  readonly errorDetail?: string | null;
  readonly transcript: readonly TranscriptEntry[];
  readonly isLive: boolean;
  readonly dataTestId?: string;
}

type RenderItem =
  | { readonly kind: 'text'; readonly text: string; readonly key: string }
  | {
      readonly kind: 'tool';
      readonly tool: ToolExecution;
      readonly key: string;
    };

/**
 * Coalesce consecutive text entries into a single block so the agent's
 * streamed output doesn't render with a hard linebreak between every
 * micro-chunk emitted by the LLM provider.
 */
function buildRenderItems(transcript: readonly TranscriptEntry[]): RenderItem[] {
  const items: RenderItem[] = [];
  let buffer = '';
  let bufferStartIdx = -1;
  const flush = () => {
    if (bufferStartIdx >= 0) {
      items.push({ kind: 'text', text: buffer, key: `t-${bufferStartIdx}` });
      buffer = '';
      bufferStartIdx = -1;
    }
  };
  transcript.forEach((entry, idx) => {
    if (entry.type === 'text') {
      if (bufferStartIdx < 0) bufferStartIdx = idx;
      buffer += entry.text;
    } else {
      flush();
      items.push({
        kind: 'tool',
        // Chat's ToolChip shape so the dock renders the same chips
        // (spinner / status icon + expandable code preview) as the
        // Conversation page.
        tool: {
          tool: entry.tool,
          toolUseId: entry.toolUseId ?? undefined,
          status: entry.status === 'completed' ? 'complete' : 'executing',
          success: entry.success,
          summary: entry.summary,
          approvalArgs: entry.args,
        },
        key: `c-${idx}`,
      });
    }
  });
  flush();
  return items;
}

const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({
  errorMessage,
  errorDetail = null,
  transcript,
  isLive,
  dataTestId = 'node-detail-panel',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // True when the user is at (or within {@link AT_BOTTOM_TOLERANCE_PX}
  // of) the bottom of the transcript. Single source of truth for
  // "auto-scroll on growth"; honours a manual scroll-up so we don't
  // yank the user back down every time a new chunk arrives.
  const isAtBottomRef = useRef(true);
  // Tracks the previous {@code isLive} value so the live-edge effect
  // fires only on the false→true transition (start of a fresh run) and
  // not on every re-render while live.
  const wasLiveRef = useRef(false);
  const items = useMemo(() => buildRenderItems(transcript), [transcript]);

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsedPref);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsedPref(next);
      return next;
    });
  }, []);

  // Track the user's scroll position so a manual scroll-up disables
  // the auto-stick. Re-engages the moment they return to the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      isAtBottomRef.current =
        scrollTop + clientHeight >= scrollHeight - AT_BOTTOM_TOLERANCE_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [collapsed]);

  // Stick to the bottom on any content growth — covers the case the
  // previous {@code [items.length]} dep missed, where streamed text
  // chunks accumulate into the LAST coalesced block without changing
  // the items array length. ResizeObserver fires after layout so
  // {@code scrollHeight} is already the new value.
  useEffect(() => {
    const inner = innerRef.current;
    const scroll = scrollRef.current;
    if (!inner || !scroll) return undefined;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;
      scroll.scrollTop = scroll.scrollHeight;
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, [collapsed]);

  // Snap to the bottom on the false→true {@code isLive} edge — a fresh
  // run resets the user's view to the live tail even if they had
  // scrolled up while reading the previous run's transcript. Mirrors
  // the chat-side "new streaming session" behaviour.
  useEffect(() => {
    if (isLive && !wasLiveRef.current) {
      isAtBottomRef.current = true;
      const scroll = scrollRef.current;
      if (scroll) scroll.scrollTop = scroll.scrollHeight;
    }
    wasLiveRef.current = isLive;
  }, [isLive]);

  // Only null means "no failure". An empty error_message on a FAILED node
  // is still a failure, and falsiness would hide it behind the idle state.
  const hasError = errorMessage != null;
  const idle = !hasError && transcript.length === 0 && !isLive;

  return (
    <LogCard data-testid={dataTestId} $collapsed={collapsed}>
      <LogHeader>
        <LogHeaderLeft>
          {isLive && <Spinner size="sm" label="Agent working" />}
          <LogTitle>Agent Log</LogTitle>
        </LogHeaderLeft>
        <CollapseToggle
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand agent log' : 'Collapse agent log'}
          title={collapsed ? 'Expand agent log' : 'Collapse agent log'}
          data-testid={`${dataTestId}__toggle`}
        >
          {collapsed ? <MdExpandMore /> : <MdExpandLess />}
        </CollapseToggle>
      </LogHeader>

      {!collapsed && hasError && (
        <ErrorScroll data-testid={`${dataTestId}__error-scroll`}>
          <MessageBox
            dataTestId={`${dataTestId}__error`}
            variant={MESSAGE_BOX_VARIANT_ERROR}
          >
            {errorMessage?.trim() || GENERIC_NODE_FAILURE}
          </MessageBox>
        </ErrorScroll>
      )}

      {!collapsed && hasError && errorDetail && (
        <NodeSection
          title="View technical details"
          dataTestId={`${dataTestId}__technical`}
        >
          <DetailScroll>
            <TextBlock data-testid={`${dataTestId}__technical-body`}>
              {errorDetail}
            </TextBlock>
          </DetailScroll>
        </NodeSection>
      )}

      {!collapsed && idle && (
        <EmptyState
          size={EMPTY_STATE_SIZE_XS}
          title="No agent activity for this node yet"
        />
      )}

      {!collapsed && !idle && (
        transcript.length === 0 && isLive ? (
          <EmptyState
            size={EMPTY_STATE_SIZE_XS}
            title="Waiting for agent output…"
          />
        ) : (
          <TranscriptScroll ref={scrollRef}>
            <TranscriptInner ref={innerRef}>
              {items.map((item, idx) => {
                if (item.kind === 'text') {
                  return (
                    <TextBlock key={item.key} data-testid={`${dataTestId}__text-${idx}`}>
                      {item.text}
                    </TextBlock>
                  );
                }
                return (
                  <ToolRow key={item.key} data-testid={`${dataTestId}__tool-${idx}`}>
                    <ToolChip tool={item.tool} />
                  </ToolRow>
                );
              })}
            </TranscriptInner>
          </TranscriptScroll>
        )
      )}
    </LogCard>
  );
};

export default React.memo(NodeDetailPanel);
